import Button from 'components/Button';
import { notifyError } from 'components/Notify/Notify';
import {
  getFilterdQuestions,
  QuestionsFrom,
} from 'components/SurveyResponses/ResponseForm/ResponseForm';
import BlurModal from 'containers/Profile/Panels/DocumentManager/BlurModal';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICompany } from 'store/companies/companiesTypes';
import { ITags } from 'store/providers/providerTypes';
import {
  IAnswersView,
  IAnswerView,
  IQuestionsView,
  ISurveyResponseView,
  ISurveyViewWithData,
  QuestionTypes,
} from 'store/survey/surveyTypes';
import styled from 'styled-components';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateResponse: (newResponse: ISurveyResponseView) => any;
  survey: ISurveyViewWithData;
  response: ISurveyResponseView | undefined;
  tags?: ITags['data'] | null;
  questionFollowupInfo: Record<string, string>;
  company?: ICompany;
}

const FollowupQuestionsContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ImageContainer = styled.img`
  height: 15rem;
  width: 15rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  object-fit: cover;
  margin: 0 auto;
`;

const Content = styled.div`
  width: 100%;
`;

const QuestionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FollowupQuestionsModal: FC<IProps> = ({
  isOpen,
  onClose,
  onUpdateResponse,
  survey,
  response,
  tags,
  questionFollowupInfo,
  company,
}) => {
  const { t } = useTranslation('survey');

  const title = questionFollowupInfo?.['title'] ?? '';
  const image = questionFollowupInfo?.['image'] ?? '';
  const content = questionFollowupInfo?.['content'] ?? '';
  const showFollowup = questionFollowupInfo?.['showFollowUp'] === 'true';
  const submitFollowUp = questionFollowupInfo?.['submitFollowUp'] === 'true';
  const questionTags = questionFollowupInfo?.['tags']?.split(',') || [];

  const [answers, setAnswers] = useState<Record<number, IAnswerView>>({});
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    setAnswers(
      (response?.surveyAnswerList || []).reduce(
        (tmp: Record<number, IAnswerView>, answer) => {
          tmp[answer.questionId] = answer;
          return tmp;
        },
        {}
      )
    );
  }, [isOpen]);

  const questions =
    survey.data.versions?.[survey.data.currentVersion!]?.questionMap;

  const handleChangeAnswer = (answer: IAnswerView, questionId: number) => {
    setAnswers((prevState) => ({ ...prevState, [questionId]: answer }));
  };

  const arrayIntersection = (arr1: Array<string>, arr2: Array<string>) => {
    const set2 = new Set(arr2);
    return arr1.filter((x) => set2.has(x));
  };

  const questionsList = Object.values(questions || {}).filter(
    (question) =>
      arrayIntersection(
        questionTags,
        question.tags?.map((el) => el.text)
      ).length
  );

  const filteredQuestions = getFilterdQuestions(
    questionsList,
    answers,
    survey.data,
    undefined,
    undefined,
    true
  );

  const { inForm, outForm } = filteredQuestions.reduce(
    (current, item) => {
      if (item.type !== 'text' || item.inputType !== 'none') {
        return {
          inForm: [...current.inForm, ...current.outForm, item],
          outForm: [],
        };
      }
      current.outForm.push(item);
      return current;
    },
    { inForm: [] as IQuestionsView[], outForm: [] as IQuestionsView[] }
  );
  const handleSaveResponse = async (e: any) => {
    e.preventDefault();
    setSaving(true);
    const answerList = Object.entries(answers).reduce(
      (list: ISurveyResponseView['surveyAnswerList'], answer: any) => {
        const [qId, response] = answer;
        const questionId = qId * 1;
        const question = questions![questionId];
        if (!question) return list;
        const answerView: IAnswersView = {
          ...response,
          questionId,
          type: question.type,
          selected:
            ((question.type === 'multipleChoice' &&
              question.useTags &&
              question.goDeepInTags) ??
            (0 > 0 && (question.showInTitle || question.distinctive)))
              ? response.selected?.reduce(
                  (acc: [], ans: string) => [...acc, ...ans.split(',')],
                  []
                )
              : response.selected,
        };
        list.push(answerView);
        return list;
      },
      []
    );

    const newResponse = {
      ...response,
      surveyAnswerList: answerList,
    } as ISurveyResponseView;
    try {
      await onUpdateResponse(newResponse);
      onClose();
    } catch (err) {
      notifyError('');
    } finally {
      setSaving(false);
    }
  };

  const handleIgnoreFollowUp = () => {
    onClose();
  };

  const renderFormPart = (questions: IQuestionsView[]) => {
    return (
      <>
        {questions.map((question) => {
          const QuestionComponent =
            QuestionsFrom[question.type as QuestionTypes];
          const answer = answers?.[question.id];
          return (
            <QuestionComponent
              disabled={false}
              key={question.id}
              tags={tags}
              answer={answer}
              isUnique={false}
              onChange={handleChangeAnswer}
              question={question}
              distinctiveValues={{}}
              isRequired={question.answerRequired}
              canChangeData={false}
              company={company}
              isEditor={false}
              survey={survey.data}
              dataSourceValues={{}}
              draft={false}
            />
          );
        })}
      </>
    );
  };
  return (
    <BlurModal
      isOpen={isOpen}
      onCloseModal={onClose}
      title={title}
      maxWidth="80vw"
    >
      <FollowupQuestionsContainer>
        {image && <ImageContainer src={image} />}
        {content && <Content>{content}</Content>}
        {showFollowup && questionsList.length ? (
          <form onSubmit={handleSaveResponse}>
            <QuestionsContainer>
              {renderFormPart(inForm)}
              {submitFollowUp && inForm.length > 0 && (
                <div className="flex flex-row-reverse gap-3">
                  <Button color="red-outline" onClick={handleIgnoreFollowUp}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" color="primary-color" loading={saving}>
                    {t('submit')}
                  </Button>
                </div>
              )}
              {renderFormPart(outForm)}
              {(!submitFollowUp || inForm.length === 0) && (
                <div className="flex flex-row-reverse gap-3">
                  <Button color="green-outline" onClick={handleIgnoreFollowUp}>
                    {t('close')}
                  </Button>
                </div>
              )}
            </QuestionsContainer>
          </form>
        ) : (
          <></>
        )}
      </FollowupQuestionsContainer>
    </BlurModal>
  );
};

export default FollowupQuestionsModal;
